import { useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HanziWriter from 'hanzi-writer';
import { useLearner } from '../../hooks/useLearner';
import { updateCharacterState } from '../../api/characters';
import { generateWorksheet } from '../../api/worksheets';
import { useDelight } from '../../contexts/DelightContext';
import NavTabSelector from '../../components/common/NavTabSelector';
import s from './CharacterProfilePage.module.css';

function useStateOptions() {
  const { t } = useTranslation();
  return [
    { key: 'to_learn', label: t('common.new') },
    { key: 'practicing', label: t('common.practicing') },
    { key: 'known', label: t('common.known') },
  ];
}

function useProfileTabs() {
  const { t } = useTranslation();
  return [
    { key: 'character', label: t('characterProfile.characterTab') },
    { key: 'writing', label: t('characterProfile.writingTab') },
  ];
}

export default function CharacterProfileContent({ character: initialCharacter, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: activeLearner } = useLearner();

  const queryClient = useQueryClient();
  const { enqueueDelight } = useDelight();
  const learnerId = activeLearner?.id;
  const STATE_OPTIONS = useStateOptions();
  const PROFILE_TABS = useProfileTabs();

  const [character, setCharacter] = useState(initialCharacter || null);
  const [stateUpdating, setStateUpdating] = useState(false);
  const [stateError, setStateError] = useState(null);
  const [mnemonicLoading, setMnemonicLoading] = useState(false);
  const [strokePracticeLoading, setStrokePracticeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('character');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const writerRef = useRef(null);

  useEffect(() => {
    setCharacter(initialCharacter);
    setActiveTab('character');
    setStateDropdownOpen(false);
  }, [initialCharacter]);

  useEffect(() => {
    if (!character?.char || !writerRef.current) return;
    writerRef.current.innerHTML = '';
    const writer = HanziWriter.create(writerRef.current, character.char, {
      width: 150,
      height: 150,
      padding: 5,
      showOutline: true,
      strokeAnimationSpeed: 1,
      delayBetweenStrokes: 300,
    });
    // HanziWriter doesn't set a viewBox — add one so CSS scaling works
    const svg = writerRef.current.querySelector('svg');
    if (svg) {
      svg.setAttribute('viewBox', '0 0 150 150');
      svg.removeAttribute('width');
      svg.removeAttribute('height');
    }

    writer.loopCharacterAnimation();
    return () => {
      if (writerRef.current) writerRef.current.innerHTML = '';
    };
  }, [character?.char]);

  async function handleStateChange(newState) {
    const childId = activeLearner?.id;
    const charId = character?.character_id || character?.id;
    if (!childId || !charId) return;
    setStateUpdating(true);
    setStateError(null);
    try {
      const result = await updateCharacterState(childId, charId, newState);
      if (result?.progression) {
        enqueueDelight(result.progression);
      }
      setCharacter((prev) => ({ ...prev, state: newState }));
      // Invalidate caches so workbook list and dashboard reflect the new state
      queryClient.invalidateQueries({ queryKey: ['childCharacters', learnerId] });
      queryClient.invalidateQueries({ queryKey: ['curriculumCharacters', learnerId] });
      queryClient.invalidateQueries({ queryKey: ['learnerProfile', learnerId] });
    } catch (err) {
      setStateError(err?.message || 'Failed to update. Please try again.');
    } finally {
      setStateUpdating(false);
      setStateDropdownOpen(false);
    }
  }

  async function handleDownloadMnemonicSheet() {
    const learnerId = activeLearner?.id;
    const charId = character?.character_id || character?.id;
    if (!learnerId || !charId) return;
    setMnemonicLoading(true);
    try {
      const result = await generateWorksheet({
        learnerId,
        characterIds: [charId],
        worksheetType: 'heuristic',
      });
      const worksheetId = result.id || result.worksheet_id;
      navigate(`/workbook/preview/${worksheetId}`, { state: { worksheet: result } });
    } catch (err) {
      alert(err.message || 'Failed to generate mnemonic sheet. Please try again.');
    } finally {
      setMnemonicLoading(false);
    }
  }

  async function handleCreateStrokePracticeSheet() {
    const learnerId = activeLearner?.id;
    const charId = character?.character_id || character?.id;
    if (!learnerId || !charId) return;
    setStrokePracticeLoading(true);
    try {
      const result = await generateWorksheet({
        learnerId,
        characterIds: [charId],
        worksheetType: 'stroke_practice',
      });
      const worksheetId = result.id || result.worksheet_id;
      navigate(`/workbook/preview/${worksheetId}`, { state: { worksheet: result } });
    } catch (err) {
      alert(err.message || 'Failed to generate stroke practice sheet. Please try again.');
    } finally {
      setStrokePracticeLoading(false);
    }
  }

  if (!character) return null;

  const state = character.state || 'to_learn';
  const stateLabel = STATE_OPTIONS.find((o) => o.key === state)?.label || state;

  return (
    <div className={s.content}>
      <h1 className="srOnly">
        Character: {character.char}
      </h1>

      {/* Character card — frosted glass */}
      <div className={s.card}>
        {/* State dropdown — all three states, all characters (ADR-032) */}
        <div className={s.dropdownWrapper}>
          <button
            type="button"
            className={s.dropdownButton}
            onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
            onKeyDown={(e) => { if (e.key === 'Escape') setStateDropdownOpen(false); }}
            disabled={stateUpdating}
            aria-expanded={stateDropdownOpen}
            aria-haspopup="listbox"
          >
            <span className={s.dropdownButtonLabel}>{stateLabel}</span>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none"
              className={`${s.dropdownChevron} ${stateDropdownOpen ? s.dropdownChevronOpen : ''}`}>
              <path d="M1 1L5 5L9 1" stroke="#211e1a" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {stateDropdownOpen && (
            <div className={s.dropdownMenu} role="listbox">
              {STATE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="option"
                  aria-selected={state === opt.key}
                  className={`${s.dropdownOption} ${state === opt.key ? s.dropdownOptionActive : ''}`}
                  onClick={() => handleStateChange(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* State update error */}
        {stateError && (
          <p role="alert" className={s.errorAlert}>
            {stateError}
          </p>
        )}

        {/* Large character */}
        <div className={s.charDisplay}>
          <span className={s.charLarge}>{character.char}</span>
          <div className={s.charMeta}>
            {character.pinyin && <span className={s.charPinyin}>{character.pinyin}</span>}
            {character.meaning && <span className={s.charMeaning}>{character.meaning}</span>}
          </div>
        </div>

        {/* Stroke order */}
        <div className={s.strokeSection}>
          <span className={s.strokeLabel}>{t('characterProfile.strokeOrder')}</span>
          <div ref={writerRef} className={s.strokeBox} />
        </div>

        {/* Tab selector */}
        <NavTabSelector
          tabs={PROFILE_TABS}
          active={activeTab}
          onChange={setActiveTab}
          className={s.navTabFull}
        />

        {/* Tab content */}
        {activeTab === 'character' && (
          <div className={s.tabCard}>
            {character.radical && (
              <div className={s.detailRow}>
                <span className={s.detailLabel}>{t('characterProfile.radical')}</span>
                <span className={s.detailValueChinese}>{character.radical}</span>
              </div>
            )}
            {character.stroke_count != null && (
              <div className={s.detailRow}>
                <span className={s.detailLabel}>{t('characterProfile.strokes')}</span>
                <span className={s.detailValue}>{character.stroke_count}</span>
              </div>
            )}
            {character.hsk_level != null && (
              <div className={s.detailRow}>
                <span className={s.detailLabel}>{t('characterProfile.hskLevel')}</span>
                <span className={s.detailValue}>{character.hsk_level}</span>
              </div>
            )}
            {/* Mnemonic sheets deactivated for v1 — re-enable after mnemonic PDF redesign
            <button
              type="button"
              className={s.actionButton}
              onClick={handleDownloadMnemonicSheet}
              disabled={mnemonicLoading}
            >
              {mnemonicLoading ? t('worksheetCreate.generating') : t('characterProfile.downloadMnemonic')}
            </button>
            */}
          </div>
        )}

        {activeTab === 'writing' && (
          <div className={`${s.tabCard} ${s.tabCardWriting}`}>
            <button
              type="button"
              className={s.actionButton}
              onClick={() => navigate('/practice', { state: { seedChars: [character] } })}
            >
              Interactive Practice
            </button>
            <button
              type="button"
              className={s.actionButton}
              onClick={handleCreateStrokePracticeSheet}
              disabled={strokePracticeLoading}
            >
              {strokePracticeLoading ? t('worksheetCreate.generating') : t('characterProfile.createStrokePractice')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
